/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file linkedinUtils.js
 * @description
 * Pure (no React / network) LinkedIn helpers shared by the mobile LinkedIn module — the twin of
 * `admin/src/features/linkedin/linkedinUtils.js`: profile-URL normalisation, the template variable set
 * shared with the e-mail composer and the HTML → plain-text conversion LinkedIn messages need.
 */

/** LinkedIn direct-message length limit. */
export const LINKEDIN_MESSAGE_LIMIT = 8000;

/** Length limit of the note attached to a connection invite. */
export const LINKEDIN_INVITE_NOTE_LIMIT = 300;

const LINKEDIN_HOST_RE = /^(?:[a-z]{2,3}\.)?linkedin\.com$/i;
const PROFILE_PATH_RE = /^\/(in|pub)\/([^/?#]+)/i;
const COMPANY_PATH_RE = /^\/(company|school)\/([^/?#]+)/i;

/**
 * Normalise a LinkedIn URL to its canonical `https://www.linkedin.com/<kind>/<id>` form.
 * @param {string} value Raw URL (with or without scheme, tracking params, trailing slash).
 * @returns {{url: string, kind: ('person'|'company'|''), id: string}} `url` is '' when it is not a profile / company URL.
 */
export const parseLinkedinUrl = (value) => {
    const none = {url: '', kind: '', id: ''};
    const raw = String(value || '').trim();

    if (!raw) {
        return none;
    }

    // Hand-parsed on purpose: React Native's `URL` polyfill does not implement `hostname` / `pathname`.
    const parts = raw.match(/^(?:https?:\/\/)?([^/?#\s]+)(\/[^?#\s]*)?/i);

    if (!parts || !LINKEDIN_HOST_RE.test(parts[1])) {
        return none;
    }

    const parsed = {pathname: parts[2] || '/'};

    const person = parsed.pathname.match(PROFILE_PATH_RE);

    if (person) {
        const id = decodeURIComponent(person[2]);

        return {url: `https://www.linkedin.com/in/${encodeURIComponent(id)}`, kind: 'person', id};
    }

    const company = parsed.pathname.match(COMPANY_PATH_RE);

    if (company) {
        const id = decodeURIComponent(company[2]);

        return {url: `https://www.linkedin.com/${company[1].toLowerCase()}/${encodeURIComponent(id)}`, kind: 'company', id};
    }

    return none;
};

/**
 * True when the value is a LinkedIn PERSON profile URL (company pages cannot receive direct messages).
 * @param {string} value Raw URL.
 * @returns {boolean}
 */
export const isLinkedinPersonUrl = (value) => parseLinkedinUrl(value).kind === 'person';

/**
 * First LinkedIn person URL found on a CRM record (lead / customer / contact), under any of the field
 * names the backend and the Apollo/lead-finder importers use.
 * @param {object} record CRM record (may be null).
 * @returns {string} Canonical profile URL or ''.
 */
export const resolveLinkedinUrl = (record) => {
    if (!record || typeof record !== 'object') {
        return '';
    }

    const candidates = [
        record.linkedinUrl,
        record.linkedin,
        record.linkedin_url,
        record.social?.linkedin,
        record.socials?.linkedin,
        record.contact?.linkedinUrl,
        record.person?.linkedinUrl,
        record.person?.linkedin_url,
        record.primaryContact?.linkedinUrl,
    ];

    for (const candidate of candidates) {
        const parsed = parseLinkedinUrl(candidate);

        if (parsed.kind === 'person') {
            return parsed.url;
        }
    }

    return '';
};

/**
 * Convert an HTML (or plain) e-mail template body into the plain text LinkedIn accepts.
 * @param {string} html Template body.
 * @returns {string}
 */
export const htmlToPlainText = (html) => {
    const text = String(html || '')
        .replace(/<\s*br\s*\/?>/gi, '\n')
        .replace(/<\/\s*(p|div|li|h[1-6]|tr)\s*>/gi, '\n')
        .replace(/<\s*li[^>]*>/gi, '- ')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'");

    return text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
};

/**
 * Template variables for a LinkedIn recipient — the same names the e-mail composer exposes plus
 * `firstName` / `lastName`.
 * @param {{name?: string, company?: string, email?: string, phone?: string, position?: string}} [recipient]
 * @param {Date} [now] Clock (injectable for tests).
 * @returns {Object<string, string>}
 */
export const buildTemplateVariables = (recipient = {}, now = new Date()) => {
    const name = String(recipient.name || '').trim();
    const [firstName = '', ...rest] = name.split(/\s+/).filter(Boolean);

    return {
        name,
        firstName,
        lastName: rest.join(' '),
        company: String(recipient.company || '').trim(),
        email: String(recipient.email || '').trim(),
        phone: String(recipient.phone || '').trim(),
        position: String(recipient.position || '').trim(),
        today: now.toLocaleDateString(),
        now: now.toLocaleString(),
    };
};

/**
 * Fill `{{variable}}` placeholders; unknown ones are removed (as the e-mail composer does).
 * @param {string} text Template text.
 * @param {Object<string, string>} variables Values by name.
 * @returns {string}
 */
export const interpolateTemplate = (text, variables = {}) => String(text || '')
    .replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (_match, key) => {
        const value = variables[key];

        return value === undefined || value === null ? '' : String(value);
    });

/**
 * Turn an e-mail template (`{subject, bodyHtml}`) into a LinkedIn message: variables filled, markup
 * stripped, subject dropped (direct messages have none).
 * @param {{bodyHtml?: string, body?: string}} template Mail template.
 * @param {Object<string, string>} variables Values by name.
 * @returns {string}
 */
export const templateToLinkedinText = (template, variables = {}) => htmlToPlainText(
    interpolateTemplate((template && (template.bodyHtml || template.body)) || '', variables),
);

/**
 * Initials for an avatar fallback.
 * @param {string} name Display name.
 * @returns {string}
 */
export const initialsOf = (name) => String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/**
 * Route target of a push / inbox notification about a LinkedIn message
 * (`metadata.eventKey === 'linkedin.message.received'`).
 * @param {object} data Push `data` / inbox metadata ({eventKey, chatId, conversationId, entityType, entityId}).
 * @returns {{name: string, params: object}|null} Navigation target or null when unrelated.
 */
export const resolveLinkedinTarget = (data) => {
    const d = data && typeof data === 'object' ? data : {};
    const eventKey = String(d.eventKey || d.eventkey || '').toLowerCase();

    if (!eventKey.startsWith('linkedin.')) {
        return null;
    }

    if (eventKey === 'linkedin.connection.accepted') {
        return {name: 'LinkedinConnectionsScreen', params: {tab: 'connections'}};
    }

    if (eventKey === 'linkedin.reconnect_needed') {
        return {name: 'LinkedinConnectScreen', params: {}};
    }

    const chatId = String(d.chatId || d.chatid || d.conversationId || d.conversationid || '').trim();

    return chatId
        ? {name: 'LinkedinConversationScreen', params: {conversationId: chatId}}
        : {name: 'LinkedinInboxScreen', params: {}};
};

/**
 * Normalise the `/linkedin/status` body.
 * @param {object} [obj] Raw body.
 * @returns {{connected: boolean, needsReconnect: boolean, ready: boolean, unreadCount: number, account: ?object, capabilities: {connections: boolean, invitations: boolean}}}
 */
export const readLinkedinState = (obj) => {
    const body = obj && typeof obj === 'object' ? obj : {};
    const caps = body.capabilities && typeof body.capabilities === 'object' ? body.capabilities : {};
    const needsReconnect = Boolean(body.needsReconnect);
    const connected = Boolean(body.connected) && !needsReconnect;

    return {
        connected,
        needsReconnect,
        // `ready` = usable right now (connected and the session is valid).
        ready: connected,
        unreadCount: Math.max(0, Number(body.unreadCount) || 0),
        account: body.account || null,
        // What the account's provider can do; connection management is hidden unless the server says so.
        capabilities: {
            connections: Boolean(caps.connections),
            invitations: Boolean(caps.invitations),
        },
    };
};

/**
 * Lead import row of a LinkedIn hit (same field names as the Apollo importer).
 * @param {object} hit Search hit.
 * @param {('people'|'companies')} kind
 * @returns {object}
 */
export const linkedinHitToLeadRow = (hit, kind) => ({
    name: hit.name || '',
    company: kind === 'companies' ? hit.name || '' : hit.company || '',
    title: kind === 'people' ? hit.headline || '' : '',
    position: kind === 'people' ? hit.headline || '' : '',
    city: hit.location || '',
    address: hit.location || '',
    linkedinUrl: hit.linkedinUrl || '',
    source: 'Other',
});

/** Backend error code -> [i18n key, Hungarian default] for the connection / invitation actions. */
const LINKEDIN_ERROR_MESSAGES = {
    LINKEDIN_INVITE_DAILY_LIMIT: ['linkedin.invite_daily_limit', 'A napi LinkedIn kapcsolatfelvételi limit elérve, próbálja holnap.'],
    LINKEDIN_INVITE_WEEKLY_LIMIT: ['linkedin.invite_weekly_limit', 'A heti LinkedIn kapcsolatfelvételi limit elérve, próbálja később.'],
    LINKEDIN_RATE_LIMITED: ['linkedin.rate_limited', 'A LinkedIn napi limitje elérve, próbálja később.'],
    LINKEDIN_SEARCH_HOURLY_LIMIT: ['linkedin.search_hourly_limit', 'A LinkedIn-keresés a fiókja védelmében szünetel, próbálja újra körülbelül egy óra múlva.'],
    LINKEDIN_SEARCH_DAILY_LIMIT: ['linkedin.search_daily_limit', 'Elérte a napi LinkedIn-keresési limitet, próbálja holnap.'],
    LINKEDIN_INVITATION_NOT_FOUND: ['linkedin.invitation_gone', 'Ez a meghívó már nincs függőben.'],
    LINKEDIN_UNSUPPORTED: ['linkedin.unsupported', 'Ez a LinkedIn fióktípus nem támogatja a kapcsolatok kezelését.'],
    LINKEDIN_NOTE_TOO_LONG: ['linkedin.note_too_long', 'A megjegyzés túl hosszú.'],
};

/**
 * User-facing text of a failed connection / invitation call.
 * @param {*} error Thrown value (an Error carrying `code` / `status`).
 * @param {(key: string, fallback: string) => string} t i18n translate function.
 * @returns {string}
 */
export const describeLinkedinError = (error, t) => {
    const code = error?.code || error?.payload?.code;
    const known = LINKEDIN_ERROR_MESSAGES[code];

    if (known) {
        return t(known[0], known[1]);
    }

    return error?.message || t('linkedin.action_failed', 'A művelet nem sikerült.');
};

/**
 * Quota line of the invitation lists ("sent today 3/20 - this week 9/80").
 * @param {?{usedToday: number, dailyLimit: number, usedWeek: number, weeklyLimit: number}} quota
 * @returns {?{day: string, week: string}} Null when the server sent no quota.
 */
export const readInviteQuota = (quota) => {
    if (!quota || !Number.isFinite(Number(quota.dailyLimit)) || !Number.isFinite(Number(quota.weeklyLimit))) {
        return null;
    }

    return {
        day: `${Number(quota.usedToday) || 0}/${Number(quota.dailyLimit)}`,
        week: `${Number(quota.usedWeek) || 0}/${Number(quota.weeklyLimit)}`,
    };
};
